import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Clock, CheckCircle, XCircle, Send, Inbox, AlertCircle, TrendingUp, Filter, X, ArrowRight, ChevronDown, ChevronUp, PlayCircle, HelpCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader, getCertifiedNetworkStatus } from '../services/otpAuthService';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';

interface AttoSvincolo {
  _id: string;
  targa: string;
  tipoAtto: string;
  dataAppuntamento: string;
  oraAppuntamento: string;
  status: string;
  dataInvio: string;
  studiSelezionati: any[];
  preventivi: any[];
  richiedente?: any;
  studioAssegnato?: any;
  documenti?: any[];
}

const SvincoloAttiListaPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [richiesteInviate, setRichiesteInviate] = useState<AttoSvincolo[]>([]);
  const [richiesteRicevute, setRichiesteRicevute] = useState<AttoSvincolo[]>([]);
  const [activeTab, setActiveTab] = useState<'inviate' | 'ricevute'>('ricevute');
  const [showSidebar, setShowSidebar] = useState(false); // Default false su mobile
  const [showCompletateRicevute, setShowCompletateRicevute] = useState(false);
  const [showCompletateInviate, setShowCompletateInviate] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('svincolo_lista_tour');

  const tourSteps = [
    {
      target: '#tour-svincolo-nuova',
      content: 'Clicca qui per avviare una nuova richiesta di svincolo.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-svincolo-tab-ricevute',
      content: 'In questa sezione trovi le richieste inviate dagli altri studi verso di te.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-svincolo-tab-inviate',
      content: 'Qui trovi le richieste che hai inviato tu ad altri studi.',
      placement: 'bottom' as const
    },
    {
      target: '#tour-svincolo-prossimi-passi',
      content: 'Questo pannello ti mostra le azioni che devi ancora completare, in ordine di priorità.',
      placement: 'top' as const
    },
    {
      target: '#tour-svincolo-prima-card',
      content: 'Ogni richiesta mostra targa, tipo di atto, stato e le azioni disponibili. Clicca per vedere i dettagli.',
      placement: 'top' as const
    }
  ];

  // Refs per scroll alle card
  const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  // Helper per ottenere l'ID dell'associato corrente
  const getCurrentAssociateId = () => {
    const associateData = localStorage.getItem('associate_data');
    return associateData ? JSON.parse(associateData).id : null;
  };

  // Scroll to top quando cambia il tab
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      // Verifica autenticazione
      if (!isAuthenticated()) {
        toast.error('Devi effettuare l\'accesso');
        navigate('/accesso-associati');
        return;
      }

      // Verifica stato membro certificato
      try {
        const status = await getCertifiedNetworkStatus();

        if (!status.isMember) {
          toast.error('Accesso riservato ai membri della Rete Certificata UNASCA', {
            duration: 5000,
            icon: '🔒',
          });
          navigate('/area-riservata');
          return;
        }

        if (!status.isActive) {
          toast.error('Il tuo account non è ancora stato attivato. Attendi l\'attivazione da parte della Segreteria Nazionale UNASCA.', {
            duration: 6000,
            icon: '⏳',
          });
          navigate('/area-riservata');
          return;
        }

        // Se tutto ok, carica i dati
        loadRichieste();
      } catch (error) {
        console.error('Errore verifica stato membro:', error);
        toast.error('Errore durante la verifica dello stato membro');
        navigate('/area-riservata');
      }
    };

    checkAccessAndLoadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  const loadRichieste = async () => {
    try {
      setLoading(true);

      // Carica richieste inviate
      const responseInviate = await axios.get(
        `${API_BASE_URL}/api/atti-svincolo/mie-richieste`,
        { headers: getAuthHeader() }
      );

      // Carica richieste ricevute
      const responseRicevute = await axios.get(
        `${API_BASE_URL}/api/atti-svincolo/richieste-ricevute`,
        { headers: getAuthHeader() }
      );

      const richiesteInviateData = responseInviate.data.data || [];
      const richiesteRicevuteData = responseRicevute.data.data || [];

      setRichiesteInviate(richiesteInviateData);
      setRichiesteRicevute(richiesteRicevuteData);

      // Debug: verifica struttura studioAssegnato
      const currentAssociateId = getCurrentAssociateId();
      console.log('🔍 Debug Statistiche:');
      console.log('Current Associate ID:', currentAssociateId);
      console.log('Richieste Ricevute con status assegnata:',
        richiesteRicevuteData.filter((r: any) => r.status === 'assegnata').map((r: any) => ({
          targa: r.targa,
          status: r.status,
          studioAssegnato: r.studioAssegnato,
          studioAssegnatoId: r.studioAssegnato?.associateId?._id || r.studioAssegnato?._id,
          isMe: (r.studioAssegnato?.associateId?._id || r.studioAssegnato?._id) === currentAssociateId
        }))
      );
    } catch (error: any) {
      console.error('Errore caricamento richieste:', error);
      toast.error('Errore durante il caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (richiesta: AttoSvincolo, tipo: 'inviata' | 'ricevuta') => {
    const status = richiesta.status;

    // Per richieste ricevute con status "assegnata", verifica se lo studio corrente è quello assegnato
    if (tipo === 'ricevuta' && status === 'assegnata') {
      const currentAssociateId = getCurrentAssociateId();

      // Verifica se lo studio corrente è quello assegnato
      const isStudioAssegnato = richiesta.studioAssegnato?.associateId?._id === currentAssociateId;

      if (isStudioAssegnato) {
        // Studio selezionato - badge verde
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            <CheckCircle className="w-3 h-3" />
            Sei stato selezionato
          </span>
        );
      } else {
        // Studio NON selezionato - badge grigio
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
            <XCircle className="w-3 h-3" />
            Non selezionato
          </span>
        );
      }
    }

    // Status normali
    const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
      'inviata': { label: 'Inviata', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: Send },
      'in_attesa_preventivi': { label: 'In attesa preventivi', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock },
      'preventivi_ricevuti': { label: 'Preventivi ricevuti', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', icon: Inbox },
      'assegnata': { label: 'Assegnata', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle },
      'documenti_caricati': { label: 'Documenti Caricati', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', icon: CheckCircle },
      'in_attesa_pagamento': { label: 'In Attesa Pagamento', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', icon: Clock },
      'completata': { label: 'Completata', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', icon: CheckCircle },
      'annullata': { label: 'Annullata', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig['inviata'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Funzione per scrollare a una card specifica
  const scrollToCard = (attoId: string) => {
    const cardElement = cardRefs.current[attoId];
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight temporaneo
      cardElement.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
      setTimeout(() => {
        cardElement.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
      }, 2000);
    }
  };

  // Calcola i prossimi passi da compiere
  const getProssimiPassi = () => {
    const passi: Array<{
      id: string;
      targa: string;
      azione: string;
      priorita: 'alta' | 'media' | 'bassa';
      icon: any;
      color: string;
      tipo: 'ricevuta' | 'inviata';
      directAction?: 'upload' | 'preventivo' | 'valuta-preventivi' | 'gestisci-pagamento' | 'carica-ricevuta'; // Azione diretta opzionale
    }> = [];

    const currentAssociateId = getCurrentAssociateId();

    // Analizza richieste ricevute
    richiesteRicevute.forEach(richiesta => {
      // Deve inviare preventivo
      if (richiesta.status === 'inviata' || richiesta.status === 'in_attesa_preventivi') {
        const haInviatoPreventivo = richiesta.preventivi?.some(
          (p: any) => (p.studio?._id || p.studio) === currentAssociateId
        );

        if (!haInviatoPreventivo) {
          passi.push({
            id: richiesta._id,
            targa: richiesta.targa,
            azione: 'Invia preventivo',
            priorita: 'alta',
            icon: Send,
            color: 'text-orange-600 dark:text-orange-400',
            tipo: 'ricevuta',
            directAction: 'preventivo'
          });
        }
      }

      // È stato selezionato - deve gestire la pratica
      if (richiesta.status === 'assegnata' && richiesta.studioAssegnato?.associateId?._id === currentAssociateId) {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Attendi documenti dal richiedente',
          priorita: 'media',
          icon: Clock,
          color: 'text-blue-600 dark:text-blue-400',
          tipo: 'ricevuta'
          // Nessuna azione diretta - è solo informativo
        });
      }

      // Documenti caricati - deve gestire pagamento/completamento
      if (richiesta.status === 'documenti_caricati' && richiesta.studioAssegnato?.associateId?._id === currentAssociateId) {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Gestisci pagamento e completa pratica',
          priorita: 'alta',
          icon: AlertCircle,
          color: 'text-orange-600 dark:text-orange-400',
          tipo: 'ricevuta',
          directAction: 'gestisci-pagamento'
        });
      }

      // In attesa pagamento
      if (richiesta.status === 'in_attesa_pagamento' && richiesta.studioAssegnato?.associateId?._id === currentAssociateId) {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Verifica pagamento e completa',
          priorita: 'alta',
          icon: AlertCircle,
          color: 'text-orange-600 dark:text-orange-400',
          tipo: 'ricevuta',
          directAction: 'gestisci-pagamento'
        });
      }
    });

    // Analizza richieste inviate
    richiesteInviate.forEach(richiesta => {
      // Ha ricevuto preventivi - deve valutare
      if (richiesta.status === 'preventivi_ricevuti') {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Valuta preventivi ricevuti',
          priorita: 'alta',
          icon: TrendingUp,
          color: 'text-purple-600 dark:text-purple-400',
          tipo: 'inviata',
          directAction: 'valuta-preventivi'
        });
      }

      // Preventivo accettato - deve caricare documenti
      if (richiesta.status === 'assegnata' && (!richiesta.documenti || richiesta.documenti.length === 0)) {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Carica documenti richiesti',
          priorita: 'alta',
          icon: FileText,
          color: 'text-orange-600 dark:text-orange-400',
          tipo: 'inviata',
          directAction: 'upload'
        });
      }

      // In attesa pagamento - deve caricare ricevuta
      if (richiesta.status === 'in_attesa_pagamento') {
        passi.push({
          id: richiesta._id,
          targa: richiesta.targa,
          azione: 'Effettua pagamento e carica ricevuta',
          priorita: 'alta',
          icon: AlertCircle,
          color: 'text-orange-600 dark:text-orange-400',
          tipo: 'inviata',
          directAction: 'carica-ricevuta'
        });
      }
    });

    // Ordina per priorità
    const prioritaOrdine = { alta: 0, media: 1, bassa: 2 };
    return passi.sort((a, b) => prioritaOrdine[a.priorita] - prioritaOrdine[b.priorita]);
  };

  const getTipoAttoLabel = (tipoAtto: string) => {
    const labels: Record<string, string> = {
      'articolo56': 'Art. 56',
      'miniPassaggio': 'Mini Passaggio',
      'passaggioRegolare': 'Passaggio Regolare'
    };
    return labels[tipoAtto] || tipoAtto;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Determina lo stile della card in base allo stato e al tipo
  const getCardStyle = (richiesta: AttoSvincolo, tipo: 'inviata' | 'ricevuta') => {
    const currentAssociateId = getCurrentAssociateId();
    const isStudioAssegnato = richiesta.studioAssegnato?.associateId?._id === currentAssociateId;

    // Richieste ricevute
    if (tipo === 'ricevuta') {
      if (richiesta.status === 'assegnata' && isStudioAssegnato) {
        // Sei stato selezionato - Verde brillante
        return {
          border: 'border-l-4 border-l-green-500 dark:border-l-green-400',
          bg: 'bg-gradient-to-r from-green-50 to-white dark:from-green-900/10 dark:to-gray-800',
          hover: 'hover:shadow-xl hover:shadow-green-100 dark:hover:shadow-green-900/20',
          priority: 'high'
        };
      } else if (richiesta.status === 'assegnata' && !isStudioAssegnato) {
        // Non sei stato selezionato - Grigio
        return {
          border: 'border-l-4 border-l-gray-300 dark:border-l-gray-600',
          bg: 'bg-gray-50 dark:bg-gray-800/50',
          hover: 'hover:shadow-md',
          priority: 'low'
        };
      } else if (richiesta.status === 'inviata' || richiesta.status === 'in_attesa_preventivi') {
        // Richiesta in attesa di preventivo - Arancione (AZIONE RICHIESTA)
        return {
          border: 'border-l-4 border-l-orange-500 dark:border-l-orange-400',
          bg: 'bg-gradient-to-r from-orange-50 to-white dark:from-orange-900/10 dark:to-gray-800',
          hover: 'hover:shadow-xl hover:shadow-orange-100 dark:hover:shadow-orange-900/20',
          priority: 'urgent'
        };
      } else if (richiesta.status === 'preventivi_ricevuti') {
        // Preventivo già inviato - Blu
        return {
          border: 'border-l-4 border-l-blue-500 dark:border-l-blue-400',
          bg: 'bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/10 dark:to-gray-800',
          hover: 'hover:shadow-lg hover:shadow-blue-100 dark:hover:shadow-blue-900/20',
          priority: 'medium'
        };
      }
    }

    // Richieste inviate
    if (tipo === 'inviata') {
      if (richiesta.status === 'preventivi_ricevuti') {
        // Hai ricevuto preventivi - Viola (AZIONE RICHIESTA)
        return {
          border: 'border-l-4 border-l-purple-500 dark:border-l-purple-400',
          bg: 'bg-gradient-to-r from-purple-50 to-white dark:from-purple-900/10 dark:to-gray-800',
          hover: 'hover:shadow-xl hover:shadow-purple-100 dark:hover:shadow-purple-900/20',
          priority: 'urgent'
        };
      } else if (richiesta.status === 'assegnata') {
        // Verifica se deve caricare documenti
        const deveCaricareDocumenti = !richiesta.documenti || richiesta.documenti.length === 0;

        if (deveCaricareDocumenti) {
          // Deve caricare documenti - Arancione (AZIONE RICHIESTA)
          return {
            border: 'border-l-4 border-l-orange-500 dark:border-l-orange-400',
            bg: 'bg-gradient-to-r from-orange-50 to-white dark:from-orange-900/10 dark:to-gray-800',
            hover: 'hover:shadow-xl hover:shadow-orange-100 dark:hover:shadow-orange-900/20',
            priority: 'urgent'
          };
        } else {
          // Documenti caricati - Verde
          return {
            border: 'border-l-4 border-l-green-500 dark:border-l-green-400',
            bg: 'bg-gradient-to-r from-green-50 to-white dark:from-green-900/10 dark:to-gray-800',
            hover: 'hover:shadow-lg hover:shadow-green-100 dark:hover:shadow-green-900/20',
            priority: 'medium'
          };
        }
      } else if (richiesta.status === 'inviata' || richiesta.status === 'in_attesa_preventivi') {
        // In attesa di preventivi - Giallo
        return {
          border: 'border-l-4 border-l-yellow-500 dark:border-l-yellow-400',
          bg: 'bg-gradient-to-r from-yellow-50 to-white dark:from-yellow-900/10 dark:to-gray-800',
          hover: 'hover:shadow-lg hover:shadow-yellow-100 dark:hover:shadow-yellow-900/20',
          priority: 'medium'
        };
      }
    }

    // Default
    return {
      border: 'border-l-4 border-l-gray-300 dark:border-l-gray-600',
      bg: 'bg-white dark:bg-gray-800',
      hover: 'hover:shadow-lg',
      priority: 'low'
    };
  };

  const renderRichiestaCard = (richiesta: AttoSvincolo, tipo: 'inviata' | 'ricevuta', index?: number) => {
    const cardStyle = getCardStyle(richiesta, tipo);

    // Determina se mostrare l'indicatore di azione richiesta
    const needsAction = (
      (tipo === 'ricevuta' && (richiesta.status === 'inviata' || richiesta.status === 'in_attesa_preventivi')) ||
      (tipo === 'inviata' && richiesta.status === 'preventivi_ricevuti') ||
      (tipo === 'inviata' && richiesta.status === 'assegnata' && (!richiesta.documenti || richiesta.documenti.length === 0))
    );

    return (
      <div
        key={richiesta._id}
        id={index === 0 ? 'tour-svincolo-prima-card' : undefined}
        ref={(el) => { cardRefs.current[richiesta._id] = el; }}
        className={`${cardStyle.bg} ${cardStyle.border} border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-5 ${cardStyle.hover} transition-all cursor-pointer relative overflow-hidden`}
        onClick={() => navigate(`/area-riservata/svincolo-atti/dettaglio/${richiesta._id}`)}
      >
        {/* Indicatore azione richiesta */}
        {needsAction && (
          <div className="absolute top-0 right-0 w-0 h-0 border-t-[32px] sm:border-t-[40px] border-t-orange-500 border-l-[32px] sm:border-l-[40px] border-l-transparent">
            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white absolute -top-7 sm:-top-9 right-0.5 sm:right-1" />
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              cardStyle.priority === 'urgent' ? 'bg-gradient-to-br from-orange-500 to-red-600' :
              cardStyle.priority === 'high' ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
              cardStyle.priority === 'medium' ? 'bg-gradient-to-br from-blue-500 to-purple-600' :
              'bg-gradient-to-br from-gray-400 to-gray-600'
            }`}>
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-0.5 sm:mb-1 truncate">
                {richiesta.targa}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {getTipoAttoLabel(richiesta.tipoAtto)}
              </p>
            </div>
          </div>
          {getStatusBadge(richiesta, tipo)}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-2 sm:p-3 border border-gray-100 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Appuntamento
            </p>
            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
              {formatDate(richiesta.dataAppuntamento)}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              ore {richiesta.oraAppuntamento}
            </p>
          </div>
          <div className="bg-white/50 dark:bg-gray-700/50 rounded-lg p-2 sm:p-3 border border-gray-100 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data invio</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
              {formatDate(richiesta.dataInvio)}
            </p>
          </div>
        </div>

        {/* Footer - Tipo Inviata */}
        {tipo === 'inviata' && (
          <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <span className="text-xs sm:text-sm font-bold text-blue-600 dark:text-blue-400">
                    {richiesta.studiSelezionati?.length || 0}
                  </span>
                </div>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Studi
                </span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center ${
                  (richiesta.preventivi?.length || 0) > 0
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <span className={`text-xs sm:text-sm font-bold ${
                    (richiesta.preventivi?.length || 0) > 0
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    {richiesta.preventivi?.length || 0}
                  </span>
                </div>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Preventivi
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Footer - Tipo Ricevuta */}
        {tipo === 'ricevuta' && richiesta.richiedente && (
          <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs sm:text-sm font-bold text-purple-600 dark:text-purple-400">
                  {richiesta.richiedente.nominativo?.charAt(0) || 'R'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Richiesta da</p>
                <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {richiesta.richiedente.nominativo || 'N/D'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento richieste...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate('/area-riservata')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                  Svincolo atti di vendita
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1 hidden sm:block">
                  Gestisci le tue richieste di svincolo
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={startTour}
                className="p-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors flex-shrink-0"
                title="Guida"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowVideoModal(true)}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-xs sm:text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex-shrink-0"
                title="Guarda il video tutorial"
              >
                <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden lg:inline">Video Tutorial</span>
                <span className="hidden sm:inline lg:hidden">Video</span>
              </button>
              <button
                id="tour-svincolo-nuova"
                onClick={() => navigate('/area-riservata/svincolo-atti')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Nuova Richiesta</span>
                <span className="sm:hidden">Nuova</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Tabs e Toggle Sidebar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex gap-1 sm:gap-2 border-b border-gray-200 dark:border-gray-800 flex-1 overflow-x-auto">
            <button
              id="tour-svincolo-tab-ricevute"
              onClick={() => setActiveTab('ricevute')}
              className={`px-3 sm:px-6 py-2 sm:py-3 font-medium transition-colors relative flex items-center gap-1 sm:gap-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'ricevute'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Inbox className="w-4 h-4" />
              <span className="hidden sm:inline">Richieste Ricevute</span>
              <span className="sm:hidden">Ricevute</span>
              {richiesteRicevute.length > 0 && (
                <span className="ml-1 px-1.5 sm:px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                  {richiesteRicevute.length}
                </span>
              )}
              {activeTab === 'ricevute' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
              )}
            </button>
            <button
              id="tour-svincolo-tab-inviate"
              onClick={() => setActiveTab('inviate')}
              className={`px-3 sm:px-6 py-2 sm:py-3 font-medium transition-colors relative flex items-center gap-1 sm:gap-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'inviate'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Richieste Inviate</span>
              <span className="sm:hidden">Inviate</span>
              {richiesteInviate.length > 0 && (
                <span className="ml-1 px-1.5 sm:px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                  {richiesteInviate.length}
                </span>
              )}
              {activeTab === 'inviate' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"></div>
              )}
            </button>
          </div>

          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="w-full sm:w-auto sm:ml-4 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Filter className="w-4 h-4" />
            <span className="sm:hidden">{showSidebar ? 'Nascondi' : 'Mostra'} Statistiche</span>
            <span className="hidden sm:inline">{showSidebar ? 'Nascondi' : 'Mostra'} Filtri</span>
          </button>
        </div>

        {/* Box Prossimi Passi */}
        {(() => {
          const prossimiPassi = getProssimiPassi();
          if (prossimiPassi.length === 0) return null;

          return (
            <div id="tour-svincolo-prossimi-passi" className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-3 sm:p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      📋 Prossimi Passi
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      {prossimiPassi.length} {prossimiPassi.length === 1 ? 'azione richiesta' : 'azioni richieste'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {prossimiPassi.map((passo, index) => {
                  const Icon = passo.icon;
                  return (
                    <button
                      key={`${passo.id}-${index}`}
                      onClick={() => {
                        // Gestisci azioni dirette
                        if (passo.directAction) {
                          switch (passo.directAction) {
                            case 'upload':
                              // Vai alla pagina di upload documenti
                              navigate(`/area-riservata/svincolo-atti/upload/${passo.id}`);
                              break;
                            case 'preventivo':
                              // Vai alla pagina dettaglio con hash per sezione preventivo
                              navigate(`/area-riservata/svincolo-atti/dettaglio/${passo.id}#preventivo`);
                              break;
                            case 'valuta-preventivi':
                              // Vai alla pagina dettaglio con hash per sezione preventivi
                              navigate(`/area-riservata/svincolo-atti/dettaglio/${passo.id}#preventivi`);
                              break;
                            case 'gestisci-pagamento':
                              // Vai alla pagina dettaglio con hash per sezione gestione
                              navigate(`/area-riservata/svincolo-atti/dettaglio/${passo.id}#gestione`);
                              break;
                            case 'carica-ricevuta':
                              // Vai alla pagina dettaglio con hash per sezione pagamento
                              navigate(`/area-riservata/svincolo-atti/dettaglio/${passo.id}#pagamento`);
                              break;
                          }
                          return;
                        }

                        // Altrimenti, cambia tab se necessario e scrolla alla card
                        const targetTab = passo.tipo === 'ricevuta' ? 'ricevute' : 'inviate';
                        if (targetTab !== activeTab) {
                          setActiveTab(targetTab);
                          // Aspetta che il tab cambi prima di scrollare
                          setTimeout(() => scrollToCard(passo.id), 100);
                        } else {
                          scrollToCard(passo.id);
                        }
                      }}
                      className="w-full flex items-center justify-between p-3 sm:p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-transparent hover:border-blue-500 dark:hover:border-blue-400 transition-all group hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          passo.priorita === 'alta'
                            ? 'bg-orange-100 dark:bg-orange-900/30'
                            : passo.priorita === 'media'
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${passo.color}`} />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                            {passo.azione}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-1">
                            <span>Targa: <span className="font-mono font-bold">{passo.targa}</span></span>
                            {passo.tipo === 'ricevuta' && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                Ricevuta
                              </span>
                            )}
                            {passo.tipo === 'inviata' && (
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                                Inviata
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Layout con Sidebar */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Sidebar */}
          {showSidebar && (
            <div className="w-full lg:w-80 lg:flex-shrink-0">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 lg:sticky lg:top-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Statistiche
                  </h3>
                  <button
                    onClick={() => setShowSidebar(false)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                {/* Statistiche Richieste Ricevute */}
                {activeTab === 'ricevute' && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                        <p className="text-xs font-medium text-orange-900 dark:text-orange-100">Richiedono Azione</p>
                      </div>
                      <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                        {richiesteRicevute.filter(r => r.status === 'inviata' || r.status === 'in_attesa_preventivi').length}
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                        In attesa di preventivo
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <p className="text-xs font-medium text-green-900 dark:text-green-100">Sei Stato Selezionato</p>
                      </div>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                        {richiesteRicevute.filter(r => {
                          const currentAssociateId = getCurrentAssociateId();
                          // Verifica sia con _id che con associateId._id per compatibilità
                          const studioAssegnatoId = r.studioAssegnato?.associateId?._id || r.studioAssegnato?._id;
                          return r.status === 'assegnata' && studioAssegnatoId === currentAssociateId;
                        }).length}
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                        Pratiche assegnate a te
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Inbox className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <p className="text-xs font-medium text-blue-900 dark:text-blue-100">Preventivo Inviato</p>
                      </div>
                      <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {richiesteRicevute.filter(r => r.status === 'preventivi_ricevuti').length}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        In attesa di risposta
                      </p>
                    </div>
                  </div>
                )}

                {/* Statistiche Richieste Inviate */}
                {activeTab === 'inviate' && (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <p className="text-xs font-medium text-purple-900 dark:text-purple-100">Richiedono Azione</p>
                      </div>
                      <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                        {richiesteInviate.filter(r => r.status === 'preventivi_ricevuti').length}
                      </p>
                      <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                        Preventivi da valutare
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <p className="text-xs font-medium text-green-900 dark:text-green-100">Assegnate</p>
                      </div>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                        {richiesteInviate.filter(r => r.status === 'assegnata').length}
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                        Studio selezionato
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                        <p className="text-xs font-medium text-yellow-900 dark:text-yellow-100">In Attesa</p>
                      </div>
                      <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                        {richiesteInviate.filter(r => r.status === 'inviata' || r.status === 'in_attesa_preventivi').length}
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        Preventivi in arrivo
                      </p>
                    </div>
                  </div>
                )}

                {/* Legenda Colori */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Legenda Colori
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded"></div>
                      <span className="text-gray-600 dark:text-gray-400">Richiede azione urgente</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded"></div>
                      <span className="text-gray-600 dark:text-gray-400">Selezionato / Assegnato</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-gray-600 dark:text-gray-400">In attesa</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded"></div>
                      <span className="text-gray-600 dark:text-gray-400">Non selezionato / Archiviato</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1">
            {/* Content */}
            {activeTab === 'ricevute' && (
              <div>
                {richiesteRicevute.length === 0 ? (
                  <div className="text-center py-12">
                    <Inbox className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Nessuna richiesta ricevuta
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Le richieste ricevute da altri colleghi appariranno qui
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Richieste Attive */}
                    {(() => {
                      const richiesteAttive = richiesteRicevute.filter(r => r.status !== 'completata' && r.status !== 'annullata');
                      return richiesteAttive.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {richiesteAttive.map((richiesta, idx) => renderRichiestaCard(richiesta, 'ricevuta', idx))}
                        </div>
                      ) : null;
                    })()}

                    {/* Richieste Completate - Accordion */}
                    {(() => {
                      const richiesteCompletate = richiesteRicevute.filter(r => r.status === 'completata' || r.status === 'annullata');
                      if (richiesteCompletate.length === 0) return null;

                      return (
                        <div className="mt-6">
                          <button
                            onClick={() => setShowCompletateRicevute(!showCompletateRicevute)}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              <span className="font-semibold text-gray-900 dark:text-white">
                                Pratiche Completate ({richiesteCompletate.length})
                              </span>
                            </div>
                            {showCompletateRicevute ? (
                              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            )}
                          </button>

                          {showCompletateRicevute && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                              {richiesteCompletate.map((richiesta, idx) => renderRichiestaCard(richiesta, 'ricevuta', idx))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {activeTab === 'inviate' && (
              <div>
                {richiesteInviate.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Nessuna richiesta inviata
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Inizia creando una nuova richiesta di svincolo
                    </p>
                    <button
                      onClick={() => navigate('/area-riservata/svincolo-atti')}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                      Nuova Richiesta
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Richieste Attive */}
                    {(() => {
                      const richiesteAttive = richiesteInviate.filter(r => r.status !== 'completata' && r.status !== 'annullata');
                      return richiesteAttive.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                          {richiesteAttive.map((richiesta, idx) => renderRichiestaCard(richiesta, 'inviata', idx))}
                        </div>
                      ) : null;
                    })()}

                    {/* Richieste Completate - Accordion */}
                    {(() => {
                      const richiesteCompletate = richiesteInviate.filter(r => r.status === 'completata' || r.status === 'annullata');
                      if (richiesteCompletate.length === 0) return null;

                      return (
                        <div className="mt-6">
                          <button
                            onClick={() => setShowCompletateInviate(!showCompletateInviate)}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                              <span className="font-semibold text-gray-900 dark:text-white">
                                Pratiche Completate ({richiesteCompletate.length})
                              </span>
                            </div>
                            {showCompletateInviate ? (
                              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            )}
                          </button>

                          {showCompletateInviate && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                              {richiesteCompletate.map((richiesta, idx) => renderRichiestaCard(richiesta, 'inviata', idx))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modale Video Tutorial */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Modale */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <PlayCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    Video Tutorial
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Come funziona il servizio di svincolo atti
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Video Container */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full border-0"
                src="https://www.youtube.com/embed/FNyXh5w3ur4?si=krjbjcPnbiVrcqcI"
                title="Video Tutorial Svincolo Atti"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            {/* Footer Modale */}
            <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                💡 <strong>Suggerimento:</strong> Guarda il video per scoprire tutte le funzionalità del servizio di svincolo atti
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SvincoloAttiListaPage;

